import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  TableContainer,
  StyledTable,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeadCell,
  LoadingContainer,
  FilterIcon,
  SortIcon,
  DragHandleCell,
  DragHandleButton,
  DragHandleIcon,
} from "./Table.styles";
import Paginator from "../Paginator";
import { TableProps, DragHandleRenderProps } from "./Table.types";
import { PaginatorWrapper } from "./Table.styles";
import { ProgressSpinner } from "primereact/progressspinner";
import sortArrow from "../../assets/icons/sortArrow.svg";
import filterIcon from "../../assets/icons/filterIcon.svg";
import Calendar from "../Calendar/Calendar";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import grabbingIcon from "../../assets/grabbing.svg";

import {
  DndContext,
  PointerSensor,
  closestCenter,
  DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import FilterCheckboxList from "../FilterCheckboxList/FilterCheckboxList";
import { reorderArray } from "../../utils/reorder";

type SortableRowDescriptor = {
  id: string;
  index: number;
  isSpecial: boolean;
  isDraggable: boolean;
};

type SortableRenderProps = {
  setNodeRef: (element: HTMLElement | null) => void;
  attributes: ReturnType<typeof useSortable>["attributes"];
  listeners: ReturnType<typeof useSortable>["listeners"];
  transform: ReturnType<typeof useSortable>["transform"];
  transition: ReturnType<typeof useSortable>["transition"];
  isDragging: boolean;
};

interface SortableTableRowProps {
  id: string;
  disabled: boolean;
  children: (props: SortableRenderProps) => React.ReactElement;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({ id, disabled, children }) => {
  const sortable = useSortable({ id, disabled });

  return children({
    setNodeRef: sortable.setNodeRef,
    attributes: sortable.attributes,
    listeners: sortable.listeners,
    transform: sortable.transform,
    transition: sortable.transition,
    isDragging: sortable.isDragging,
  });
};

const DefaultDragHandle: React.FC<DragHandleRenderProps> = ({
  attributes,
  listeners,
  isDragging,
  isDisabled,
}) => {
  const interactionProps = isDisabled
    ? {}
    : {
        ...(attributes ?? {}),
        ...(listeners ?? {}),
      };

  return (
    <DragHandleButton
      type="button"
      aria-label="Перетащить строку"
      data-dragging={isDragging ? "true" : undefined}
      disabled={isDisabled}
      {...interactionProps}
    >
      <DragHandleIcon src={grabbingIcon} aria-hidden="true" alt="" />
    </DragHandleButton>
  );
};

const resolveRowId = (
  row: Record<string, any>,
  index: number,
  getRowId?: TableProps["getRowId"],
) => {
  if (typeof getRowId === "function") {
    return getRowId(row, index);
  }

  if (row && (row.id !== undefined || row.id === 0)) {
    return String(row.id);
  }

  if (row && (row.key !== undefined || row.key === 0)) {
    return String(row.key);
  }

  return `row-${index}`;
};

const Table: React.FC<TableProps> = ({
  headers,
  rows,
  currentPage,
  onSort,
  onCalendarChange,
  filterStartDate,
  filterEndDate,
  handleActiveFilterChange,
  activeFilter,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  rowAmountOptions,
  onRowExpand,
  isExpanded,
  loading,
  minHeight = "115px",
  specialRowKey = "title",
  expandedRowIndex,
  appliedFilters,
  onFilterSave,
  onFilterReset,
  remHeight = 134,
  isDraggable = false,
  getRowId,
  onReorder,
  renderDragHandle,
  isRowDraggable,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const calendarRef = useRef<HTMLDivElement>(null);
  const filterListRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(calendarRef, () => {
    if (handleActiveFilterChange) {
      handleActiveFilterChange(null);
    }
  });

  useOnClickOutside(filterListRef, () => {
    if (handleActiveFilterChange) {
      handleActiveFilterChange(null);
    }
  });

  useEffect(() => {
    if (typeof expandedRowIndex === "number") {
      setExpandedRows(new Set([expandedRowIndex]));
    } else {
      setExpandedRows(new Set());
    }
  }, [expandedRowIndex]);

  const openFilterList = (key: string) => {
    if (handleActiveFilterChange) {
      const newFilter = activeFilter === key ? null : key;
      handleActiveFilterChange(newFilter);
    }
  };

  const hasExpandableRows = rows.some((row) => row.expandedContent);

  const openCalendar = (key: string) => {
    if (handleActiveFilterChange) {
      const newFilter = activeFilter === key ? null : key;
      handleActiveFilterChange(newFilter);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const rowIds = useMemo(
    () => rows.map((row, index) => resolveRowId(row, index, getRowId)),
    [rows, getRowId],
  );

  const rowDescriptors = useMemo<SortableRowDescriptor[]>(
    () =>
      rows.map((row, index) => {
        const isSpecial = Boolean(row[specialRowKey]?.props?.colSpan);
        const draggableByProp = isRowDraggable ? isRowDraggable(row, index) : true;

        return {
          id: rowIds[index],
          index,
          isSpecial,
          isDraggable: isDraggable && !isSpecial && draggableByProp,
        };
      }),
    [rows, rowIds, specialRowKey, isRowDraggable, isDraggable],
  );

  const sortableRowIds = useMemo(
    () => rowDescriptors.filter((descriptor) => !descriptor.isSpecial).map((descriptor) => descriptor.id),
    [rowDescriptors],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!onReorder) {
        return;
      }

      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const sortableDescriptors = rowDescriptors.filter((descriptor) => !descriptor.isSpecial);
      const activeDescriptor = sortableDescriptors.find((descriptor) => descriptor.id === active.id);
      const overDescriptor = sortableDescriptors.find((descriptor) => descriptor.id === over.id);

      if (!activeDescriptor || !overDescriptor) {
        return;
      }

      const fromIndex = activeDescriptor.index;
      const toIndex = overDescriptor.index;

      if (fromIndex === toIndex) {
        return;
      }

      const reorderedRows = reorderArray(rows, fromIndex, toIndex);
      const reorderedIds = arrayMove(rowIds, fromIndex, toIndex);

      onReorder({
        orderedIds: reorderedIds,
        items: reorderedRows,
      });
    },
    [onReorder, rowDescriptors, rows, rowIds],
  );

  const renderHandle = useCallback(
    (props: DragHandleRenderProps) => {
      if (typeof renderDragHandle === "function") {
        return renderDragHandle(props);
      }

      return <DefaultDragHandle {...props} />;
    },
    [renderDragHandle],
  );

  const totalColumns = headers.length + 1 + (isDraggable ? 1 : 0) + (hasExpandableRows ? 1 : 0);

  const tableRows = useMemo(() => {
    if (loading) {
      return [
        <TableRow key="loading">
          <TableCell colSpan={totalColumns}>
            <LoadingContainer>
              <ProgressSpinner style={{ width: "50px", height: "50px", color: "#6A0DAD" }} />
            </LoadingContainer>
          </TableCell>
        </TableRow>,
      ];
    }

    return rows.map((row, rowIndex) => {
      const descriptor = rowDescriptors[rowIndex];
      const expandedColSpan = Math.max(totalColumns - 1, 1);

      if (row[specialRowKey]?.props?.colSpan) {
        return <React.Fragment key={`${descriptor.id}-special`}>{row[specialRowKey]}</React.Fragment>;
      }

      const expandedContent =
        expandedRows.has(rowIndex) && row.expandedContent ? (
          <TableRow key={`${descriptor.id}-expanded`}>
            <TableCell />
            <TableCell colSpan={expandedColSpan}>{row.expandedContent}</TableCell>
          </TableRow>
        ) : null;

      if (!isDraggable) {
        return (
          <React.Fragment key={descriptor.id}>
            <TableRow isExpanded={expandedRows.has(rowIndex)}>
              {headers.map((header, colIndex) => (
                <TableCell
                  key={colIndex}
                  isHidden={
                    expandedRows.has(rowIndex) &&
                    header.hidden === true &&
                    !row.isDeleted
                  }
                >
                  <div className="content">{header.key ? row[header.key] : null}</div>
                </TableCell>
              ))}
            </TableRow>
            {expandedContent}
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={descriptor.id}>
          <SortableTableRow id={descriptor.id} disabled={!descriptor.isDraggable}>
            {({
              setNodeRef,
              attributes,
              listeners,
              transform,
              transition,
              isDragging,
            }) => {
              const style: React.CSSProperties = {
                transform: transform ? CSS.Transform.toString(transform) : undefined,
                transition,
              };

              return (
                <TableRow ref={setNodeRef} isExpanded={expandedRows.has(rowIndex)} style={style}>
                  <DragHandleCell data-disabled={!descriptor.isDraggable}>
                    <div className="content">
                      {renderHandle({
                        row,
                        index: rowIndex,
                        attributes,
                        listeners,
                        isDragging,
                        isDisabled: !descriptor.isDraggable,
                      })}
                    </div>
                  </DragHandleCell>
                  {headers.map((header, colIndex) => (
                    <TableCell
                      key={colIndex}
                      isHidden={
                        expandedRows.has(rowIndex) &&
                        header.hidden === true &&
                        !row.isDeleted
                      }
                    >
                      <div className="content">{header.key ? row[header.key] : null}</div>
                    </TableCell>
                  ))}
                </TableRow>
              );
            }}
          </SortableTableRow>
          {expandedContent}
        </React.Fragment>
      );
    });
  }, [
    expandedRows,
    headers,
    isDraggable,
    loading,
    renderHandle,
    rowDescriptors,
    rows,
    specialRowKey,
    totalColumns,
  ]);

  return (
    <TableContainer isExpanded={isExpanded} minHeight={minHeight} remHeight={remHeight}>
      <StyledTable>
        <TableHeader>
          <tr>
            {isDraggable && <th style={{ width: "64px" }} aria-hidden="true" />}
            {headers.map((header, index) => (
              <th key={index} style={{ width: header.width, position: "relative" }}>
                <TableHeadCell
                  key={index}
                  onClick={
                    header.sortable !== false
                      ? () => onSort?.(header.fieldName || "")
                      : undefined
                  }
                  style={{
                    cursor: header.sortable === false ? "default" : "pointer",
                    width: headers[index].width,
                  }}
                >
                  {header.sortable !== false && header.key && (
                    <SortIcon src={sortArrow} alt="Sort" />
                  )}

                  <span>{header.label}</span>

                  {header.isFilter && header.filterType === "calendar" && (
                    <FilterIcon
                      src={filterIcon}
                      alt="Filter"
                      style={{ cursor: "pointer", marginLeft: "8px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openCalendar(header.key || "");
                      }}
                    />
                  )}

                  {header.isFilter && header.filterType === "list" && (
                    <FilterIcon
                      src={filterIcon}
                      alt="Filter"
                      style={{ cursor: "pointer", marginLeft: "8px" }}
                      onClick={(e) => {
                        e.stopPropagation(); 
                        openFilterList(header.key || "");
                      }}
                    />
                  )}
                </TableHeadCell>
                {header.isFilter && header.filterType === "calendar" && (
                  <>
                    {activeFilter === header.key && (
                      <div
                        ref={calendarRef}
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          zIndex: 10,
                        }}
                      >
                        <Calendar
                          startDate={filterStartDate || undefined}
                          endDate={filterEndDate || undefined}
                          isRange={true}
                          onDateChange={onCalendarChange}
                        />
                      </div>
                    )}
                  </>
                )}
                {header.isFilter && header.filterType === "list" && (
                  <>
                    {activeFilter === header.key && (
                      <div
                        ref={filterListRef}
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          zIndex: 10,
                        }}
                      >
                        <FilterCheckboxList
                          options={
                            header.listOptions ?? []
                          }
                          selectedOptions={appliedFilters?.[header.key || ""] || []}
                          onSave={(selected) => onFilterSave?.(header.key || "", selected)}
                          onReset={() => onFilterReset?.(header.key || "")}
                        />
                      </div>
                    )}
                  </>
                )}
              </th>
            ))}
            {hasExpandableRows && <th />}
          </tr>
        </TableHeader>
        {isDraggable && !loading ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortableRowIds} strategy={verticalListSortingStrategy}>
              <TableBody>{tableRows}</TableBody>
            </SortableContext>
          </DndContext>
        ) : (
          <TableBody>{tableRows}</TableBody>
        )}
      </StyledTable>
      {totalPages > 1 && (
        <PaginatorWrapper>
          <Paginator
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            rowAmountOptions={rowAmountOptions}
          />
        </PaginatorWrapper>
      )}
    </TableContainer>
  );
};

export default Table;
